// User-customisable accent / "active main color" (Alex 2026-06-22).
//
// `--accent` and its derivatives ship as static tokens in variables.css
// (default orange #D97706). This module lets the user override the base
// colour at runtime — we set the CSS custom properties on :root, derive
// the hover shade + the two alpha tints from the chosen hex, and persist
// the choice. Everything that reads `var(--accent*)` (active session
// stripe, badges, segmented highlights, …) recolours instantly.

const KEY = 'cloud-accent-color';
const SKEY = 'cloud-success-color';
export const ACCENT_DEFAULT = '#D97706';
export const SUCCESS_DEFAULT = '#16A34A';

// A few sensible presets for the Settings swatches (the user can still
// pick any colour via the native picker).
export const ACCENT_PRESETS = [
  '#D97706', // orange (default)
  '#DC2626', // red
  '#16A34A', // green
  '#2563EB', // blue
  '#7C3AED', // violet
  '#DB2777', // pink
  '#0891B2', // cyan
  '#475569', // slate
];

// Success / "secondary" colour presets (ready dots, send-voice, OK badges).
export const SUCCESS_PRESETS = [
  '#16A34A', // green (default)
  '#059669', // emerald
  '#0D9488', // teal
  '#65A30D', // lime
  '#2563EB', // blue
  '#D97706', // orange
  '#7C3AED', // violet
  '#475569', // slate
];

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  let h = hex.trim().replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function toHex(n: number): string {
  return Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
}

// Darken toward black by `amount` (0..1) for the :hover/:active shade.
function darken(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  const f = 1 - amount;
  return `#${toHex(r * f)}${toHex(g * f)}${toHex(b * f)}`;
}

/** Write the accent custom properties onto :root from a base hex. */
export function applyAccent(color: string): void {
  const root = document.documentElement;
  const { r, g, b } = hexToRgb(color);
  root.style.setProperty('--accent', color);
  root.style.setProperty('--accent-hover', darken(color, 0.16));
  root.style.setProperty('--accent-bg', `rgba(${r}, ${g}, ${b}, 0.08)`);
  root.style.setProperty('--accent-bg-strong', `rgba(${r}, ${g}, ${b}, 0.15)`);
}

export function getAccent(): string {
  try { return localStorage.getItem(KEY) || ACCENT_DEFAULT; } catch { return ACCENT_DEFAULT; }
}

/** Persist + apply + broadcast so any open Settings instance stays in sync. */
export function setAccent(color: string): void {
  try { localStorage.setItem(KEY, color); } catch { /* quota */ }
  applyAccent(color);
  window.dispatchEvent(new CustomEvent('cloud-accent-change', { detail: color }));
}

/** Write the success custom properties onto :root from a base hex. */
export function applySuccess(color: string): void {
  const root = document.documentElement;
  const { r, g, b } = hexToRgb(color);
  root.style.setProperty('--color-success', color);
  root.style.setProperty('--color-success-bg', `rgba(${r}, ${g}, ${b}, 0.10)`);
}

export function getSuccess(): string {
  try { return localStorage.getItem(SKEY) || SUCCESS_DEFAULT; } catch { return SUCCESS_DEFAULT; }
}

export function setSuccess(color: string): void {
  try { localStorage.setItem(SKEY, color); } catch { /* quota */ }
  applySuccess(color);
  window.dispatchEvent(new CustomEvent('cloud-accent-change', { detail: color }));
}

// ── Light / Dark theme (Stage 1, Alex 2026-06-22) ──────────────────
export type ThemeMode = 'light' | 'dark' | 'system';
const TKEY = 'cloud-theme';

function prefersDark(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  return mode === 'system' ? (prefersDark() ? 'dark' : 'light') : mode;
}

export function getThemeMode(): ThemeMode {
  try {
    const v = localStorage.getItem(TKEY);
    return v === 'dark' || v === 'system' ? v : 'light';
  } catch { return 'light'; }
}

/** Write the resolved theme onto <html data-theme="…">. */
export function applyThemeMode(mode: ThemeMode): void {
  document.documentElement.setAttribute('data-theme', resolveTheme(mode));
}

export function setThemeMode(mode: ThemeMode): void {
  try { localStorage.setItem(TKEY, mode); } catch { /* quota */ }
  applyThemeMode(mode);
  window.dispatchEvent(new CustomEvent('cloud-theme-change', { detail: mode }));
}

/** Call once at startup (before first paint) to restore the saved colours
 *  + theme, and keep `system` mode in sync with the OS preference. */
export function initTheme(): void {
  applyAccent(getAccent());
  applySuccess(getSuccess());
  applyThemeMode(getThemeMode());
  // Follow the OS when the user is on `system`.
  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (getThemeMode() === 'system') applyThemeMode('system');
  });
}
